import { useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from '@/lib/motion';

/**
 * Ambient background for the sign-in brand panel: slowly drifting nodes joined by faint links,
 * with an occasional SmartKargo-orange signal travelling along a link. One canvas, no React
 * re-renders per frame. It pauses when the tab is hidden or the panel is off-screen, and draws a
 * single still frame when the user prefers reduced motion.
 */
export function SignalField() {
  const ref = useRef<HTMLCanvasElement>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const ORANGE = '224, 91, 38';
    const LINK = 150; // px: nodes closer than this are joined
    let w = 0;
    let h = 0;
    let dpr = 1;
    type Node = { x: number; y: number; vx: number; vy: number; r: number };
    let nodes: Node[] = [];
    // A signal travels from node a to node b; t runs 0 → 1.
    let signals: { a: number; b: number; t: number; speed: number }[] = [];
    const pointer = { x: 0, y: 0, tx: 0, ty: 0 };

    const seed = () => {
      // Density scales with area, capped so large monitors stay cheap.
      const count = Math.max(18, Math.min(64, Math.round((w * h) / 16000)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.12,
        vy: (Math.random() - 0.5) * 0.12,
        r: Math.random() < 0.18 ? 1.8 : 1.1,
      }));
      signals = [];
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
      draw(0);
    };

    const draw = (dt: number) => {
      ctx.clearRect(0, 0, w, h);
      // Very small parallax: the field leans at most ~6px toward the pointer.
      pointer.x += (pointer.tx - pointer.x) * 0.04;
      pointer.y += (pointer.ty - pointer.y) * 0.04;
      const ox = pointer.x * 6;
      const oy = pointer.y * 6;

      for (const n of nodes) {
        n.x += n.vx * dt;
        n.y += n.vy * dt;
        if (n.x < -20) n.x = w + 20;
        if (n.x > w + 20) n.x = -20;
        if (n.y < -20) n.y = h + 20;
        if (n.y > h + 20) n.y = -20;
      }

      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]!;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > LINK * LINK) continue;
          const alpha = (1 - Math.sqrt(d2) / LINK) * 0.09;
          ctx.strokeStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(a.x + ox, a.y + oy);
          ctx.lineTo(b.x + ox, b.y + oy);
          ctx.stroke();
        }
      }

      for (const n of nodes) {
        ctx.fillStyle = n.r > 1.5 ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.16)';
        ctx.beginPath();
        ctx.arc(n.x + ox, n.y + oy, n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Signals: rare, slow, at most three at once.
      if (dt > 0 && signals.length < 3 && Math.random() < 0.006) {
        const a = Math.floor(Math.random() * nodes.length);
        let best = -1;
        let bestD = LINK * LINK;
        for (let j = 0; j < nodes.length; j++) {
          if (j === a) continue;
          const dx = nodes[a]!.x - nodes[j]!.x;
          const dy = nodes[a]!.y - nodes[j]!.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < bestD && d2 > 900) {
            bestD = d2;
            best = j;
          }
        }
        if (best >= 0) signals.push({ a, b: best, t: 0, speed: 0.0045 + Math.random() * 0.003 });
      }
      signals = signals.filter((s) => s.t <= 1);
      for (const s of signals) {
        s.t += s.speed * dt;
        const a = nodes[s.a]!;
        const b = nodes[s.b]!;
        const x = a.x + (b.x - a.x) * s.t + ox;
        const y = a.y + (b.y - a.y) * s.t + oy;
        const fade = Math.sin(Math.min(1, s.t) * Math.PI);
        ctx.strokeStyle = `rgba(${ORANGE},${(0.35 * fade).toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(a.x + ox, a.y + oy);
        ctx.lineTo(x, y);
        ctx.stroke();
        const g = ctx.createRadialGradient(x, y, 0, x, y, 7);
        g.addColorStop(0, `rgba(${ORANGE},${(0.9 * fade).toFixed(3)})`);
        g.addColorStop(1, `rgba(${ORANGE},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    if (reduced) return () => ro.disconnect();

    let raf = 0;
    let last = performance.now();
    let visible = true;
    const loop = (now: number) => {
      // dt in 60fps frames, clamped so a background tab does not jump the field.
      const dt = Math.min(3, (now - last) / 16.67);
      last = now;
      draw(dt);
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (raf || !visible || document.hidden) return;
      last = performance.now();
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };
    const io = new IntersectionObserver(([e]) => {
      visible = !!e?.isIntersecting;
      if (visible) start();
      else stop();
    });
    io.observe(canvas);
    const onVis = () => (document.hidden ? stop() : start());
    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      pointer.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pointermove', onMove, { passive: true });
    start();
    return () => {
      stop();
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pointermove', onMove);
    };
  }, [reduced]);

  return <canvas ref={ref} aria-hidden className="pointer-events-none absolute inset-0 -z-10 h-full w-full" />;
}
